<template>
  <div ref="component" class="home-solutions--component-container">
    <div class="home-solutions--component">
      <div class="home-solutions--container">
        <div class="home-solutions--heading">
          <div class="home-solutions--subtitle">
            <p>Solutions & Accelerators</p>
          </div>
          <div class="home-solutions--title">
            <h2>We Solve Problems Once. Forever.</h2>
          </div>
          <div class="home-solutions--description">
            <p class="home-solutions--description-paragraph">When we see our clients regularly come up against similar challenges, we follow that pattern to the source—building Solutions and Accelerators that let you migrate, modernize, and monetize your data faster than ever before.</p>
          </div>
        </div>
        <div class="home-solutions--arrows">
          <img @click="prev" class="home-solutions--arrows-image svg-white" src="https://hakkoda.io/wp-content/uploads/2024/11/solution-left-arrow.svg" alt=""/>
          <img @click="next" class="home-solutions--arrows-image svg-white" src="https://hakkoda.io/wp-content/uploads/2024/11/solution-rigth-arrow.svg" alt=""/>
        </div>
      </div>
      <div class="home-solutions--container">
        <div class="home-solutions--swiper-overlay">
          <swiper
            :slidesPerView="'auto'"
            :spaceBetween="20"
            :loop="true"
            :slideShadows="true"
            @swiper="onSwiper"
            class="solutionsSwiper"
          >
            <swiper-slide
              v-for="(solution, index) in conf" 
            >
              <div 
                class="home-solutions--item" 
                @mouseover="mouseOver(solution)"
              >
                <div class="home-solutions--card-title-container">
                  <img
                    class="home-solutions--logo"
                    :src="`${ solution.logo }`"
                    :alt="`${ solution.solutionName }`"
                  />
                  <h3 class="home-solutions--card-title">{{ solution.solutionName }}</h3>
                </div>
                <div :class="`home-solutions--poster-container ${ solution.class }`">
                  <img
                    class="home-solutions--poster"
                    :class="{
                      'active': solution?.solutionName == active?.solutionName
                    }"
                    :src="`${ solution.posterImage }`"
                    :alt="`${ solution.solutionName }`"
                  />
                </div>
                <div class="home-solutions--overlay"
                  v-show="solution?.solutionName == active?.solutionName || !isLargeScreen"
                >
                    <div class="home-solutions--overlay-container">
                      <a
                        class="underline-decoration"
                        :href="`${ solution.target }`"
                      >LEARN MORE</a>
                      <a 
                        :href="`${ solution.target }`"
                      >
                      <img class="home-solutions--overlay-arrow" src="https://hakkoda.io/wp-content/uploads/2024/11/arrow-up-right.svg" alt="Watch the full story"/>
                      </a>
                    </div>
                </div>
                <div class="home-solutions--card-description">
                  <div v-show="solution?.sector" class="home-solutions--cloud-container" >
                    <span class="home-solutions--cloud">{{ solution.sector }}</span>
                  </div>
                  <p class="home-solutions--card-paragraph">{{ solution.description }}</p>
                </div>
              </div>
            </swiper-slide>
          </swiper>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, onMounted, onUnmounted, computed } from 'vue'
    import { useMediaQuery } from '@vueuse/core'
  import { Swiper, SwiperSlide } from 'swiper/vue';

  interface Props {
  }

  const props = defineProps<Props>()
  const conf = ref([
    {
      solutionName: 'AI Accelerated Migrations',
      logo: 'https://hakkoda.io/wp-content/uploads/2025/03/ai.svg',
      posterImage: 'https://hakkoda.io/wp-content/uploads/2025/03/thumb_ai_migs-1.jpg',
      industry: '',
      //sector: 'Business Intelligence',
      wistiaId: '',
      description: 'Hakkoda’s refactored migration roadmap slashes timelines, delivers stronger returns on investment, and powers innovation with a mix of certified data talent and AI accelerators.',
      target: 'https://hakkoda.io/ai-accelerated-migrations/',
      //class: 'gradient-5'
    },
    {
      solutionName: 'BI Modernization',
      logo: 'https://hakkoda.io/wp-content/uploads/2025/03/bimodern.svg',
      posterImage: 'https://hakkoda.io/wp-content/uploads/2024/12/sigma-solutions-3.png',
      industry: '',
      //sector: 'Business Intelligence',
      wistiaId: '',
      description: 'Hakkoda’s three-stage approach to modernizing Business Intelligence drives efficiency in auditing, rationalizing, and migrating your analytics to a modern BI environment like Sigma.',
      target: 'https://hakkoda.io/3-phase-bi-rationalization-migration-with-hakkoda-ai/'
    },
    {
      solutionName: 'Clinical Research',
      logo: 'https://hakkoda.io/wp-content/uploads/2025/03/clinica.svg',
      posterImage: 'https://hakkoda.io/wp-content/uploads/2025/03/thumb_clinical-1.jpg',
      industry: '',
      //sector: 'Business Intelligence',
      wistiaId: '',
      description: 'Hakkoda’s research accelerators streamline TRE management, integrate public papers with clinical data in Snowflake, and enhance collaboration, making clinical research more efficient and insightful.',
      target: 'https://hakkoda.io/solutions/clinical-research/'
    },
    {
      solutionName: 'Data Enablement',
      logo: 'https://hakkoda.io/wp-content/uploads/2025/03/enablement.svg',
      posterImage: 'https://hakkoda.io/wp-content/uploads/2025/03/thumb_enablement-1.jpg',
      industry: '',
      //sector: 'Business Intelligence',
      wistiaId: 'xib90it0te',
      description: 'Hakkoda’s Data Enablement tools improve data governance, streamline master data management, and foster collaboration across systems, ensuring data is accurate, accessible, and compliant.',
      target: 'https://hakkoda.io/solutions/data-enablement/'
    },
    {
      solutionName: 'EHR to Cloud',
      logo: 'https://hakkoda.io/wp-content/uploads/2025/03/epic2cloud.svg',
      posterImage: 'https://hakkoda.io/wp-content/uploads/2025/03/thumb_EHR.jpg',
      industry: '',
      //sector: 'Business Intelligence',
      wistiaId: '',
      description: 'Hakkoda’s EHR to Snowflake solution simplifies ingesting proprietary data from Epic and Cerner, enabling seamless integration and providing a unified view across the patient lifecycle.',
      target: 'https://hakkoda.io/solutions/ehr-to-cloud/'
    },
    {
      solutionName: 'FHIR Data Transformation',
      logo: 'https://hakkoda.io/wp-content/uploads/2025/03/fhir.svg',
      posterImage: 'https://hakkoda.io/wp-content/uploads/2025/03/thumb_FHIR-1.jpg',
      industry: '',
      //sector: 'Business Intelligence',
      wistiaId: '',
      description: 'Hakkoda’s FHIR solutions simplify healthcare data integration and analytics by standardizing FHIR bundles, exporting structured data, and converting it into the OMOP common data model.',
      target: 'https://hakkoda.io/solutions/fhir-data-transformation/'
    },
    {
      solutionName: 'Healthcare & Patient Outcomes',
      logo: 'https://hakkoda.io/wp-content/uploads/2025/03/outcomes.svg',
      posterImage: 'https://hakkoda.io/wp-content/uploads/2025/03/thumb_outcomes-1.jpg',
      industry: '',
      //sector: 'Business Intelligence',
      wistiaId: '',
      description: 'Hakkoda’s healthcare solutions use data science and machine learning to improve care, reduce costs, and optimize operations by eliminating silos and powering real-time, predictive insights.',
      target: 'https://hakkoda.io/solutions/healthcare-operations-and-patient-outcomes/'
    },
    {
      solutionName: 'Investment Analytics',
      logo: 'https://hakkoda.io/wp-content/uploads/2024/11/mamba.svg',
      posterImage: 'https://hakkoda.io/wp-content/uploads/2025/03/thumb_investements-1.jpg',
      //sector: 'Business Analytics',
      wistiaId: 'kw2tn9fj5p',
      description: 'Hakkoda’s Investment Analytics solutions empower organizations with advanced data analytics to streamline portfolio management, optimize risk analysis, and improve decision-making for better performance tracking.',
      target: 'https://hakkoda.io/solutions/investment-analytics/'
    },
    {
      solutionName: 'SAP Migration & Optimization',
      logo: 'https://hakkoda.io/wp-content/uploads/2025/03/sap.svg',
      posterImage: 'https://hakkoda.io/wp-content/uploads/2025/03/thumb_sap-1-1.jpg',
      //sector: 'Document Management',
      wistiaId: '',
      description: 'Hakkoda’s SAP migration accelerators help enterprises quickly migrate analytics workloads to Snowflake, unifying data for scalable, enterprise-wide analytics with a single source of truth.',
      target: 'https://hakkoda.io/sap/'
    },
    {
      solutionName: 'Snowflake Optimization',
      logo: 'https://hakkoda.io/wp-content/uploads/2025/03/snowflake.svg',
      posterImage: 'https://hakkoda.io/wp-content/uploads/2025/03/thumb_snowflake-1.jpg',
      //sector: 'Data Governance',
      wistiaId: 'fsj2z9ao4k',
      description: 'Hakkoda’s Snowflake Optimization solutions enhance performance, security, and cost-efficiency by streamlining data access, optimizing warehouse use, and providing real-time insights for improved model performance.',
      target: 'https://hakkoda.io/solutions/snowflake-optimization-solutions/'
    },
    {
      solutionName: 'Unstructured Healthcare Data',
      logo: 'https://hakkoda.io/wp-content/uploads/2025/03/unstructured.svg',
      posterImage: 'https://hakkoda.io/wp-content/uploads/2025/03/thumb_unstructured-1.jpg',
      //sector: 'Accelerated Migration',
      wistiaId: '',
      description: 'Run and execute large query batches automatically within Snowflake while generating insights on compile times, cluster size, and execution time.',
      target: 'Hakkoda’s Unstructured Data accelerators transform complex healthcare formats like HL7v2 and DICOM into structured data, enabling life-saving decisions with streamlined, Snowpark-powered solutions.',
    },
    {
      solutionName: 'Native Apps & Product Platforming',
      logo: 'https://hakkoda.io/wp-content/uploads/2025/03/nativeapps.svg',
      posterImage: 'https://hakkoda.io/wp-content/uploads/2025/03/thumb_native.jpg',
      //sector: 'Healthcare',
      wistiaId: '',
      description: 'Hakkoda’s native app and product platforming solutions empower businesses to build and scale custom, secure, high-performance applications, enhancing user experiences and facilitating seamless integration.',
      target: 'https://hakkoda.io/solutions/native-apps',
    }
  ])
  const isLargeScreen = useMediaQuery('(min-width: 992px)')
  const active = ref(null)
  const swiperRef = ref(null)

  function markVisibleSlides() {
    let swiper = swiperRef.value;
    if (!swiper) return;

    let swiperRect = swiper.el.getBoundingClientRect();

    swiper.slides.forEach(slide => {
      const slideRect = slide.getBoundingClientRect();
      const isVisible =
        slideRect.right > swiperRect.left &&
        slideRect.left < swiperRect.right;

      slide.classList.toggle('visible-slide', isVisible);
      slide.classList.remove('last-visible-slide');
    });

    // Get visible slides
    const visibleSlides = Array.from(swiper.slides).filter(slide =>
      slide.classList.contains('visible-slide')
    );

    const lastVisible = visibleSlides[visibleSlides.length - 1] || null;

    if (lastVisible) {
      lastVisible.classList.add('last-visible-slide');
    }
  }

  function onSwiper(swiperInstance) {
    swiperRef.value = swiperInstance
    markVisibleSlides()
  }

  function next () {
    swiperRef.value.slideNext()
    markVisibleSlides()
  }

  function prev () {
    swiperRef.value.slidePrev() 
    markVisibleSlides()
  }

  function mouseOver(val) {
    active.value = val
    markVisibleSlides()
  }

  onMounted( ()  => {
  })

  onUnmounted( () => {
  })
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
  .home-solutions {

    &--component {
      display: flex;
      width: 100%;
      flex-direction: column;
      max-width: 1440px;

      &-container {
        width: 100%;
        min-height: 600px;
        display: flex;
        justify-content: center;
        align-items: center;
        background: #000;
        padding: 20px;
      }

      .swiper-slide {
        width: 100%;
        max-width: 320px;

        @media (min-width: 768px) {
          max-width: 400px;
        }
      }

      .last-visible-slide {
        //filter: brightness(0.6);
      }

      .gradient-1 {
        background: linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(180deg, #000 27.75%, #6A89B7 100%);
      }

      .gradient-2 {
        background: linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(180deg, #000 27.75%, #F37121 100%);
      }

      .gradient-3 {
        background: linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(180deg, #000 27.75%, #80EAFF 100%);
      }

      .gradient-4 {
        background: linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(180deg, #000 27.75%, #6A89B7 100%);
      }

      .gradient-5 {
        background: linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(180deg, #000 27.75%, #FF694A 100%);
      }

      @media (min-width: 992px) {

        &-container {
          padding: 20px 40px;
        }
      }
    }

    &--container {
      display: flex;
      flex-grow: 1;
      flex-direction: column;
      overflow-x: hidden;
      padding-bottom: 40px;

      @media (min-width: 992px) {
        flex-direction: row;
      }
    }

    &--swiper-overlay {
      position: relative;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 2;

        @media (min-width: 992px) {
          background: linear-gradient(90deg, rgba(0,0,0,0) 60%, rgba(0,0,0,1) 85%, rgba(0,0,0,1) 100%);
        }
      }
    }

    &--item {
      cursor: pointer;
    }

    &--heading {
      padding-top: 40px;
    }

    &--logo {
      height: 28px;
      object-fit: contain;
      width: 28px;
    }

    &--title {
      padding: 10px 0;
      display: flex;
      width: 100%;
      justify-content: space-between;
      flex-direction: column;

      @media (min-width: 576px) {
        flex-direction: row;
      }

      h2 {
        color: #fff;
        font-size: 40px;
        font-style: normal;
        font-weight: 600;
        line-height: 48px;
      }
    }

    &--description {
      display: flex;
      flex-direction: column;
      padding: 20px 0;

      h3 {
        color: #fff;
        font-size: 20px;
        font-style: normal;
        font-weight: 500;
        line-height: 36px; /* 180% */
      }

      &-paragraph {
        color: #fff;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px; 
        max-width: 100%;
      }

      @media (min-width: 768px) {
        &-paragraph {
          max-width: 50%;
        }
      }
    }

    &--card {

      &-title {
        color: #FFF;
        font-size: 24px;
        font-style: normal;
        font-weight: 500;
        line-height: 1.5;
        padding: 10px 0;

        &-container {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 16px;
          padding: 20px;
          position: absolute;
          top: 0;
          z-index: 1;
        }
      }

      &-description {
        display: flex;
        flex-direction: column;
        min-height: 240px;
        padding: 30px 10px;
        gap: 30px;

        h3 {
          color: #fff;
          font-size: 20px;
          font-style: normal;
          font-weight: 500;
          line-height: 36px; /* 180% */
        }
      }

      &-paragraph {
        color: #fff;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px; 
      }
    }

    &--subtitle {
      p {
        color: #21B0E2;
        font-size: 20px;
        font-style: normal;
        font-weight: 500;
        line-height: 32px;
      }
    }

    &--arrows {
      display: flex;
      gap: 40px;
      font-size: 30px;
      justify-content: center;

      @media (min-width: 992px) {
        margin-right: 60px;
      }

      &-image {
        width: 36px;
        height: auto;
      }
    }

    &--poster {
      width: auto;
      object-fit: cover;
      position: relative;
      transition: transform .3s ease-out;
      bottom: -10px;
      width: 100%;

      &.active {
        transform: translateY(-10px);
      }

      &-container {
        overflow: hidden;
        // margin-top: 90px;
      }
    }

    &--overlay {
      position: absolute;
      width: 100%;
      height: 150px;
      bottom: 239px;
      background: linear-gradient(0deg, rgba(0, 0, 0, 1) 0%, rgba(255, 255, 255, 0) 100%);
      

      &-container {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        width: 100%;
        height: 150px;
        align-items: end;
        padding: 40px 20px;

        a, i {
          color: #fff;
        }

        a {
          color: #FFF;
          text-align: center;
          font-size: 16px;
          font-style: normal;
          font-weight: 500;
          line-height: 14px; 
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        img {
          width: 24px;
        }
      }
    }

    &--cloud {
      border-radius: 10px;
      background: #80EAFF;
      color: #000;
      text-align: center;
      font-size: 10px;
      font-style: normal;
      font-weight: 500;
      line-height: 12px;
      text-transform: uppercase;
      padding: 4px 10px;
    }
  }
</style>